/**
 * SmarterElements
 * Core module for embeddable UI components
 * Uses iframes loading routes from the React application
 */

import { createJWT } from './utils/jwt';
import { createConfig, Environment } from '@platform-shared/config';
import { setupUnifiedParentResize, ParentResizeManager } from './utils/unified-resize';

// Default styles for the iframe container
const DEFAULT_IFRAME_STYLES: Record<string, string> = {
  border: '0',
  margin: '0',
  padding: '0',
  width: '100%',
  height: '100%',
  overflow: 'auto', // Allow scrolling when content exceeds iframe size
  display: 'block',
  background: 'transparent',
};

/**
 * Get the base URL for a specific environment from the shared configuration
 * @param env The environment name
 */
function getElementsBaseUrl(env: Environment): string {
  const config = createConfig(env);
  return config['elements']?.ui?.baseUrl || '';
}

// Create a record of environment URLs using the shared configuration
const ENVIRONMENT_URLS: Record<Environment, string> = {
  production: getElementsBaseUrl('production'),
  development: getElementsBaseUrl('development'),
  stage: getElementsBaseUrl('stage'),
  local: getElementsBaseUrl('local') || 'http://localhost:4000' // Default for local development
};

// Note: Keeping this for future use
// const DEFAULT_CONTAINER_STYLES: Record<string, string> = {
//   position: 'relative',
//   width: '100%',
//   height: '100%',
//   overflow: 'hidden',
// };

interface SmarterElementsOptions {
  publicApiKey?: string | null;
  environment?: Environment;
  baseUrl?: string;
  [key: string]: unknown;
}

interface ElementOptions {
  [key: string]: unknown;
}

interface ElementConstraints {
  minHeight?: number;
  maxHeight?: number;
  minWidth?: number;
  maxWidth?: number;
}

type EventHandler = (data?: unknown) => void;

export class SmarterElements {
  options: SmarterElementsOptions;
  private _mountedElements: Map<string, ElementInstance>;

  constructor(options: SmarterElementsOptions = {}) {
    this.options = {
      publicApiKey: null,
      environment: options.environment || 'production',
      ...options,
    };

    // if no baseUrl is provided, use the default based on the environment
    if (!options?.baseUrl) {
      const env = this.options.environment as Environment;
      this.options.baseUrl = ENVIRONMENT_URLS[env];
    }

    this._mountedElements = new Map();
  }

  /**
   * Create an element instance
   * @param type - Element type (route name)
   * @param options - Element options
   * @returns ElementInstance
   */
  create(type: string, options: ElementOptions = {}): ElementInstance {
    // Generate a random hash for the element ID (about 10 chars)
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 6);
    const randomHash = timestamp.slice(-6) + random;
    const elementId = `smarter-element-${randomHash}`;

    const instance = new ElementInstance(elementId, type, {
      ...this.options,
      ...options,
    });
    this._mountedElements.set(elementId, instance);
    return instance;
  }

  /**
   * Track a mounted element instance
   * @param elementId - The ID of the mounted element
   * @param instance - The element instance
   */
  trackMountedElement(elementId: string, instance: ElementInstance): void {
    this._mountedElements.set(elementId, instance);
  }
}

export class ElementInstance {
  elementId: string;
  type: string;
  options: Record<string, unknown>;
  container: HTMLElement | null;
  iframe: HTMLIFrameElement | null;
  iframeLoaded: boolean;
  private _eventHandlers: Record<string, EventHandler[]>;
  private _pendingUpdates: Record<string, unknown> | null;
  private _initialHeight: number = 0;
  private _cleanupResizeHandling: (() => void) | null = null;
  private _resizeManager: ParentResizeManager | null = null;
  private _environment: Environment;
  private _constraints: ElementConstraints = {};


  constructor(elementId: string, type: string, options: Record<string, unknown> = {}) {
    this.elementId = elementId;
    this.type = type;
    this.options = options;
    this.container = null;
    this._environment = (options['environment'] as Environment) || 'production';
    this.iframe = null;
    this.iframeLoaded = false;
    this._eventHandlers = {};
    this._pendingUpdates = null;
    this._initialHeight = 0;
  }

  /**
   * Mount the element to a DOM container
   * @param container - DOM element or selector
   */
  mount(container: HTMLElement | string): void {
    // Implementation of mount method
    if (typeof container === 'string') {
      const element = document.querySelector(container);
      if (!element) {
        throw new Error(`Container element not found: ${container}`);
      }
      this.container = element as HTMLElement;
    } else {
      this.container = container;
    }

    this._loadElementRoute();
  }

  /**
   * Load the element route in the iframe
   * @private
   */
  private _loadElementRoute(): void {
    if (!this.container) return;

    // Create iframe
    this.iframe = document.createElement('iframe');
    Object.assign(this.iframe.style, DEFAULT_IFRAME_STYLES);
    this.iframe.setAttribute('title', `SmarterElements: ${this.type}`);
    this.iframe.setAttribute('allow', 'camera; microphone; fullscreen');
    this.iframe.setAttribute('allowfullscreen', 'true');
    this.iframe.setAttribute('id', `${this.elementId}`);
    this.iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-orientation-lock allow-presentation');

    // Set initial height based on container dimensions if available
    if (this.container) {
      const containerHeight = this.container.offsetHeight;
      if (containerHeight > 0) {
        // Override the percentage height with a pixel value
        this._initialHeight = containerHeight;
        this.iframe.style.height = `${containerHeight}px`;
        console.log('Setting initial iframe height from container:', containerHeight);
      }
    }

    // Set up resize handling before the iframe loads
    this._setupResizeHandling();

    // Set up message event listener for other messages
    const messageHandler = (event: MessageEvent) => {
      if (!event.data || event.data.source !== 'smarter-elements') return;
      if (event.data.elementId !== this.elementId) return;

      const { type, payload } = event.data;
      console.log('Received message from iframe:', event.data);
      this.trigger(type, payload);
    };

    window.addEventListener('message', messageHandler);

    // Set up load handler
    const loadHandler = () => {
      this.iframeLoaded = true;
      this._onReady();
      if (this._pendingUpdates) {
        this.update(this._pendingUpdates);
        this._pendingUpdates = null;
      }
    };

    this.iframe.addEventListener('load', loadHandler, { once: true });

    // Set up error handler
    const errorHandler = (error: ErrorEvent) => {
      console.error('SmarterElements iframe error:', error);
      this.trigger('error', { error: error.message || 'Unknown error occurred' });
    };

    this.iframe.addEventListener('error', errorHandler as EventListener);

    // Set up cleanup function
    const cleanup = () => {
      window.removeEventListener('message', messageHandler);
      this.iframe?.removeEventListener('load', loadHandler as EventListener);
      this.iframe?.removeEventListener('error', errorHandler as EventListener);
    };

    // Append iframe to container
    this.container.innerHTML = '';
    this.container.appendChild(this.iframe);

    // Set up unload cleanup
    const unloadHandler = () => {
      cleanup();
      window.removeEventListener('unload', unloadHandler);
    };
    window.addEventListener('unload', unloadHandler);

    // Set initial source
    this.iframe.src = this._getElementUrl();
  }

  /**
   * Get the URL for the element
   * @private
   */
  private _getElementUrl(): string {
    const baseUrl = (this.options['baseUrl'] as string) || '';

    const payload: Record<string, unknown> & { elementId: string } = {
      elementId: this.elementId
    };

    // Add other options to the payload
    Object.entries(this.options).forEach(([key, value]) => {
      // Skip baseUrl and other internal options
      if (key !== 'baseUrl' && key !== 'environment' && key !== 'apiKey') {
        payload[key] = value;
      }
    });

    payload['_initialHeight'] = this._initialHeight;
    // add teh current environment from the instance
    payload['_environment'] = this._environment;
    // Create JWT token
    const token = createJWT(payload);

    return `${baseUrl}/elements/${this.type}?data=${token}`;
  }

  /**
   * Called when the element is ready
   * @private
   */
  private _onReady(): void {
    this.trigger('ready');
  }

  /**
   * Set up unified resize handling for the iframe
   * @private
   */
  private _setupResizeHandling(): void {
    if (!this.iframe) return;

    // Use the unified resize manager with manager access
    const { cleanup, manager } = setupUnifiedParentResize(
      this.iframe,
      ({ height, width }: { height: number; width: number }) => {
        this._updateIframeHeight(height);
        // Trigger resize event with both dimensions
        this.trigger('resize', { height, width, elementId: this.elementId });
      },
      {
        elementId: this.elementId,
        ...this._constraints,
      }
    );

    this._cleanupResizeHandling = cleanup;
    this._resizeManager = manager;

    // Set up message listener for other events (non-resize)
    const messageHandler = (event: MessageEvent) => {
      if (!event.data || event.source !== this.iframe?.contentWindow) return;
      if (event.data.elementId && event.data.elementId !== this.elementId) return;

      const { type, payload, event: eventName, data } = event.data;

      // Handle custom events from the element
      if (type === 'event' && eventName) {
        this.trigger(eventName, data);
      } else if (type && type !== 'resize') {
        // Handle other message types
        this.trigger(type, payload || data);
      }
    };

    window.addEventListener('message', messageHandler);

    // Store cleanup function for message handler
    const originalCleanup = this._cleanupResizeHandling;
    this._cleanupResizeHandling = () => {
      if (originalCleanup) originalCleanup();
      window.removeEventListener('message', messageHandler);
    };
  }

  /**
   * Update the iframe height with constraint enforcement
   * @param height - New height in pixels
   * @private
   */
  private _updateIframeHeight(height: number): void {
    if (!this.iframe) return;

    // Apply constraints
    const minHeight = this._constraints.minHeight || 60;
    const maxHeight = this._constraints.maxHeight || Infinity;
    let constrainedHeight = Math.max(height, minHeight);
    constrainedHeight = Math.min(constrainedHeight, maxHeight);

    // Get current height (without 'px' suffix)
    const currentHeight = parseInt(this.iframe.style.height, 10) || 0;

    // Only update if height actually changed (avoid unnecessary DOM updates)
    if (Math.abs(currentHeight - constrainedHeight) > 1) {
      // Apply smooth transition only when needed
      if (!this.iframe.style.transition) {
        this.iframe.style.transition = 'height 0.15s ease-out';
      }

      this.iframe.style.height = `${constrainedHeight}px`;
    }
  }

  /**
   * Update element options
   * @param options - New options
   */
  update(options: Record<string, unknown>): void {
    this.options = { ...this.options, ...options };

    if (!this.iframeLoaded) {
      this._pendingUpdates = options;
      return;
    }

    if (this.iframe?.contentWindow) {
      this.iframe.contentWindow.postMessage(
        {
          source: 'smarter-elements',
          type: 'update',
          elementId: this.elementId,
          options: this.options,
        },
        '*'
      );
    }
  }

  /**
   * Unmount the element
   */
  unmount(): void {
    if (this.container && this.iframe) {
      // Clean up resize handling
      if (this._cleanupResizeHandling) {
        this._cleanupResizeHandling();
        this._cleanupResizeHandling = null;
      }

      this.container.removeChild(this.iframe);
      this.iframe = null;
      this.iframeLoaded = false;
      this.trigger('unmount');
    }
  }

  /**
   * Add event listener
   * @param event - Event name
   * @param handler - Event handler
   */
  on(event: string, handler: EventHandler): void {
    if (!this._eventHandlers[event]) {
      this._eventHandlers[event] = [];
    }
    this._eventHandlers[event].push(handler);
  }

  /**
   * Remove event listener
   * @param event - Event name
   * @param handler - Event handler to remove
   */
  off(event: string, handler: EventHandler): void {
    if (!this._eventHandlers[event]) return;

    const index = this._eventHandlers[event].indexOf(handler);
    if (index > -1) {
      this._eventHandlers[event].splice(index, 1);
    }
  }

  /**
   * Trigger event
   * @param event - Event name
   * @param data - Event data
   */
  trigger(event: string, data?: unknown): void {
    if (!this._eventHandlers[event]) return;

    this._eventHandlers[event].forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in ${event} handler:`, error);
      }
    });
  }

  /**
   * Render the component
   * @private
   */
  // Marked as private and unused for now
  // private render(): void {
  //   if (!this.iframeLoaded) return;

  //   if (this.iframe?.contentWindow) {
  //     this.iframe.contentWindow.postMessage(
  //       {
  //         source: 'smarter-elements',
  //         type: 'render',
  //         elementId: this.elementId,
  //         options: this.options,
  //       },
  //       '*'
  //     );
  //   }
  // }

  /**
   * Open a modal with the element content
   * @param options - Modal options including width, height, maxWidth, maxHeight, dismissOnDocumentClick, escapeClose, zIndex
   * @returns Promise that resolves when modal is opened
   */
  async openModal(options: {
    width?: string;
    height?: string;
    maxWidth?: string;
    maxHeight?: string;
    dismissOnDocumentClick?: boolean;
    escapeClose?: boolean;
    zIndex?: number;
    padding?: string;
    [key: string]: unknown;
  } = {}): Promise<ElementInstance> {
    // Disable body scrolling
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    (this as any).originalOverflow = originalOverflow;

    // Create modal container
    const modalContainer = document.createElement('div');
    modalContainer.id = `modal-container-${this.elementId}`;
    modalContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: ${options.zIndex || 9999};
      font-family: system-ui, -apple-system, sans-serif;
    `;

    // Create modal content wrapper
    const modalContent = document.createElement('div');
    const modalPadding = options.padding || '10px';
    modalContent.style.cssText = `
      position: relative;
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      width: ${options.width || '90%'};
      ${options.height ? `height: ${options.height};` : 'height: auto;'}
      max-width: ${options.maxWidth || '800px'};
      max-height: ${options.maxHeight || '90vh'};
      overflow: ${options.height ? 'auto' : 'hidden'};
      animation: modalSlideIn 0.3s ease-out;
      padding: ${modalPadding};
      box-sizing: border-box;
    `;

    // Add modal animations
    if (!document.getElementById('modal-animations')) {
      const style = document.createElement('style');
      style.id = 'modal-animations';
      style.textContent = `
        @keyframes modalSlideIn {
          from { opacity: 0; transform: scale(0.95) translateY(-10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }

    // Handle backdrop click
    if (options.dismissOnDocumentClick !== false) {
      modalContainer.addEventListener('click', (e) => {
        if (e.target === modalContainer) {
          this.closeModal();
        }
      });
    }

    // Handle escape key
    if (options.escapeClose !== false) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          this.closeModal();
          document.removeEventListener('keydown', handleEscape);
        }
      };
      document.addEventListener('keydown', handleEscape);
      (this as any).escapeHandler = handleEscape;
    }

    // Create iframe with modal parameters
    const iframe = document.createElement('iframe');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.style.cssText = `
      width: 100%;
      height: 100%;
      border: none;
      border-radius: 12px;
    `;

    // Add modal parameters to the element URL
    const modalParams = {
      modal: 'true',
      modalWidth: options.width,
      modalHeight: options.height,
      modalMaxWidth: options.maxWidth,
      modalMaxHeight: options.maxHeight,
      modalDismissOnDocumentClick: options.dismissOnDocumentClick,
      modalEscapeClose: options.escapeClose,
      modalZIndex: options.zIndex
    };

    // Get the element URL and add modal params
    const elementUrl = this._getElementUrl();
    const url = new URL(elementUrl, window.location.origin);
    Object.entries(modalParams).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    });

    iframe.src = url.toString();

    // Set up message handling for modal iframe (including resize)
    const handleModalMessages = (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow) return;

      try {
        const { type: messageType, ...data } = event.data;
        if (data.elementId !== this.elementId) return;

        switch (messageType) {
          case 'resize':
            // Update iframe size
            const newHeight = data.height || 400;
            iframe.style.height = `${newHeight}px`;

            // Update modal content size (respecting constraints)
            const maxWidth = options.maxWidth ?
              (typeof options.maxWidth === 'number' ? options.maxWidth : parseInt(options.maxWidth)) :
              800;
            const maxHeight = options.maxHeight ?
              (typeof options.maxHeight === 'number' ? options.maxHeight :
                options.maxHeight.includes('vh') ?
                  (parseInt(options.maxHeight) / 100) * window.innerHeight :
                  parseInt(options.maxHeight)) :
              window.innerHeight * 0.9;

            // Apply width constraints
            const newWidth = data.width || modalContent.offsetWidth;
            const constrainedWidth = Math.min(newWidth, maxWidth);
            modalContent.style.width = options.width ?
              (typeof options.width === 'number' ? `${options.width}px` : options.width) :
              `${constrainedWidth}px`;

            // Apply height constraints - set explicit height to match iframe + padding
            // Parse padding value to add to iframe height (since we use box-sizing: border-box)
            const paddingValue = parseInt(modalPadding) * 2; // top + bottom padding
            const totalContentHeight = newHeight + paddingValue;
            const constrainedHeight = Math.min(totalContentHeight, maxHeight);
            modalContent.style.height = `${constrainedHeight}px`;
            modalContent.style.maxHeight = `${maxHeight}px`;

            // Trigger resize event
            this.trigger('resize', { height: newHeight, width: newWidth, elementId: this.elementId });
            break;

          case 'ready':
            this.trigger('ready', { elementId: this.elementId });
            break;

          case 'error':
            this.trigger('error', { elementId: this.elementId, error: data });
            break;

          default:
            this.trigger(messageType, data);
            break;
        }
      } catch (error) {
        console.error('Error handling modal message:', error);
      }
    };

    window.addEventListener('message', handleModalMessages);

    // Assemble modal
    modalContent.appendChild(iframe);
    modalContainer.appendChild(modalContent);
    document.body.appendChild(modalContainer);

    // Store references for cleanup
    (this as any).modalContainer = modalContainer;
    (this as any).modalIframe = iframe;
    (this as any).modalMessageHandler = handleModalMessages;

    // Trigger modal open event
    this.trigger('modal:open', { elementId: this.elementId });

    return this;
  }

  /**
   * Close the modal
   * @returns The element instance for chaining
   */
  closeModal(): ElementInstance {
    const modalContainer = (this as any).modalContainer;
    if (modalContainer && modalContainer.parentNode) {
      // Restore body scrolling
      const originalOverflow = (this as any).originalOverflow;
      if (originalOverflow !== undefined) {
        document.body.style.overflow = originalOverflow;
        (this as any).originalOverflow = undefined;
      }

      // Remove escape key handler
      if ((this as any).escapeHandler) {
        document.removeEventListener('keydown', (this as any).escapeHandler);
        (this as any).escapeHandler = null;
      }

      // Remove modal message handler
      if ((this as any).modalMessageHandler) {
        window.removeEventListener('message', (this as any).modalMessageHandler);
        (this as any).modalMessageHandler = null;
      }

      // Add fade out animation
      modalContainer.style.opacity = '0';
      modalContainer.style.transition = 'opacity 0.2s ease-out';

      // Remove after animation
      setTimeout(() => {
        if (modalContainer.parentNode) {
          modalContainer.parentNode.removeChild(modalContainer);
        }
        (this as any).modalContainer = null;
        (this as any).modalIframe = null;
      }, 200);

      // Trigger modal close event
      this.trigger('modal:close', { elementId: this.elementId });
    }

    return this;
  }

  /**
   * Update element constraints (min/max dimensions)
   * @param constraints - New constraints to apply
   * @returns The element instance for chaining
   */
  updateConstraints(constraints: ElementConstraints): ElementInstance {
    this._constraints = { ...this._constraints, ...constraints };

    // Apply constraints to iframe immediately
    if (this.iframe) {
      const { minHeight, maxHeight, minWidth, maxWidth } = this._constraints;

      if (minHeight !== undefined) {
        this.iframe.style.minHeight = `${minHeight}px`;
      }
      if (maxHeight !== undefined) {
        this.iframe.style.maxHeight = `${maxHeight}px`;
      }
      if (minWidth !== undefined) {
        this.iframe.style.minWidth = `${minWidth}px`;
      }
      if (maxWidth !== undefined) {
        this.iframe.style.maxWidth = `${maxWidth}px`;
      }

      // Always ensure iframe doesn't exceed parent container
      this.iframe.style.maxWidth = '100%';
    }

    // Update the resize manager with new constraints
    if (this._cleanupResizeHandling) {
      // Recreate resize handling with new constraints
      this._cleanupResizeHandling();
      this._setupResizeHandling();
    }

    // Trigger constraint update event
    this.trigger('constraints:update', { constraints: this._constraints, elementId: this.elementId });

    return this;
  }

  /**
   * Manually trigger size recalculation
   * Useful when container size changes (e.g., window resize, sidebar toggle)
   * @returns The element instance for chaining
   */
  recalculateSize(): ElementInstance {
    if (this._resizeManager) {
      this._resizeManager.recalculateSize();
    } else {
      console.warn('Cannot recalculate size: resize manager not available. Element may not be mounted.');
    }
    return this;
  }

  /**
   * Get current element constraints
   * @returns Current constraints
   */
  getConstraints(): ElementConstraints {
    return { ...this._constraints };
  }
}

/**
 * Create a SmarterElements instance
 * @param options - Configuration options
 * @returns A new SmarterElements instance
 */
export function createSmarterElements(options: SmarterElementsOptions = {}): SmarterElements {
  return new SmarterElements(options);
}

// Export default for backward compatibility
export default createSmarterElements;
